import {
  Accessibility,
  Co2,
  EditOutlined,
  SpaOutlined,
} from '@mui/icons-material';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Sector } from '@/domain/impact/impact.entity';
import { CalculateFestivalCo2 } from '@/domain/impact-calculate-festival/entities/calculate-festival-co2.entity';
import { CalculateFestivalEnv } from '@/domain/impact-calculate-festival/entities/calculate-festival-env.entity';
import { CalculateFestivalEss } from '@/domain/impact-calculate-festival/entities/calculate-festival-ess.entity';
import { language, translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { Fonts } from '@/ui/theme/fonts';
import { getColorForScore, ScoreColor } from '@/ui/utils/get-color-for-score';

import { prepareData } from './prepare-scores-by-sector-data';

export function ScoresBySectorCards({
  calculateCo2,
  calculateEnv,
  calculateEss,
  projectId,
}: {
  calculateCo2: CalculateFestivalCo2;
  calculateEnv: CalculateFestivalEnv;
  calculateEss: CalculateFestivalEss;
  projectId: number;
}) {
  const data = prepareData(calculateCo2, calculateEnv, calculateEss);
  return (
    <Stack spacing={2} divider={<Divider />}>
      {data.map((x) => (
        <SectorScoreCard
          key={x.title}
          title={x.title}
          sector={x.sector}
          scores={x.scores}
          projectId={projectId}
        />
      ))}
    </Stack>
  );
}

function SectorScoreCard({
  title,
  scores,
  sector,
  projectId,
}: {
  title: string;
  sector: Sector;
  scores: {
    co2: number;
    env: string;
    ess: string;
  };
  projectId: number;
}) {
  return (
    <Stack spacing={1} sx={{ paddingX: 2 }}>
      <TitleAndOpeningButton
        title={title}
        sector={sector}
        projectId={projectId}
      />
      <ScoreLines scores={scores} />
    </Stack>
  );
}

function TitleAndOpeningButton({
  title,
  sector,
  projectId,
}: {
  title: string;
  sector: Sector;
  projectId: number;
}) {
  const navigate = useNavigate();
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
    >
      <Typography sx={{ fontWeight: '900' }}>{title}</Typography>
      <Box
        sx={{
          backgroundColor: ColorsRaw.anthracite.Anthracite_3,
          borderRadius: '0.75rem',
        }}
      >
        <IconButton
          onClick={() =>
            navigate(
              `/app/projects/festival/${projectId}/sector?type=${sector}`,
            )
          }
        >
          <EditOutlined sx={{ fontSize: 18 }} color="primary" />
        </IconButton>
      </Box>
    </Stack>
  );
}

function ScoreLines({
  scores,
}: {
  scores: {
    co2: number;
    env: string;
    ess: string;
  };
}) {
  const colorCo2 = {
    text: ColorsRaw.basic.White,
    main: ColorsRaw.anthracite.Anthracite_5,
    secondary: ColorsRaw.scores.Antracite_4012,
  };
  const colorEnv = getColorForScore(scores.env);
  const colorESS = getColorForScore(scores.ess);
  return (
    <Stack spacing={1} divider={<Divider />}>
      <ScoreLine
        icon={<Co2 />}
        name={translate('Projects.teCO2')}
        score={scores.co2.toLocaleString(language)}
        color={colorCo2}
      />
      <ScoreLine
        icon={<SpaOutlined />}
        name={translate('Projects.env')}
        score={scores.env}
        color={colorEnv}
      />
      <ScoreLine
        icon={<Accessibility />}
        name={translate('Projects.ess')}
        score={scores.ess}
        color={colorESS}
      />
    </Stack>
  );
}

function ScoreLine({
  icon,
  name,
  score,
  color,
}: {
  icon: React.ReactNode;
  name: string;
  score: string | number;
  color: ScoreColor;
}) {
  return (
    <Stack direction={'row'} alignItems={'center'} spacing={2}>
      {icon}
      <Box flexGrow={'1'}>
        <Typography color="text.secondary">{name}</Typography>
      </Box>
      <Box
        sx={{
          color: color.main,
          backgroundColor: color.secondary,
          px: 3,
          borderRadius: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: '900',
            fontFamily: Fonts.DarkerGrotesque,
          }}
        >
          {score}
        </Typography>
      </Box>
    </Stack>
  );
}
